import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MaterialModule} from '../../../material.module';
import {
    Webinar,
    WebinarAnalyticsResponse,
    WebinarAnalyticsSummary,
    WebinarAnalyticsUserMetric,
} from '../../../models/webinar.model';

interface AnalyticsKpi {
    label: string;
    value: string;
    hint: string;
}

@Component({
    selector: 'app-webinar-analytics-panel',
    standalone: true,
    imports: [CommonModule, MaterialModule],
    templateUrl: './webinar-analytics-panel.component.html',
    styleUrls: ['./webinar-analytics-panel.component.scss'],
})
export class WebinarAnalyticsPanelComponent {
    @Input() selectedWebinar: Webinar | null = null;
    @Input() analytics: WebinarAnalyticsResponse | null = null;
    @Input() isLoading = false;
    @Input() errorMessage: string | null = null;

    @Output() refresh = new EventEmitter<void>();

    readonly userColumns = ['attendee', 'registered', 'attended', 'watchTime', 'completion', 'engagement'];

    get summary(): WebinarAnalyticsSummary | null {
        return this.analytics?.summary ?? null;
    }

    get users(): WebinarAnalyticsUserMetric[] {
        return this.analytics?.users ?? [];
    }

    get generatedAtLabel(): string {
        if (!this.analytics?.generatedAt) return '';
        return this.formatDateTime(this.analytics.generatedAt);
    }

    get kpis(): AnalyticsKpi[] {
        const summary = this.summary;
        if (!summary) return [];
        return [
            {
                label: 'Registrations',
                value: this.formatNumber(summary.registrationCount),
                hint: `${this.formatNumber(summary.attendeeCount)} attended`,
            },
            {
                label: 'Attendance rate',
                value: this.formatPercent(summary.attendanceRate),
                hint: `${this.formatNumber(summary.noShowCount)} no-show`,
            },
            {
                label: 'Average watch time',
                value: this.formatDuration(summary.averageDurationSeconds),
                hint: `${this.formatDuration(summary.medianDurationSeconds)} median`,
            },
            {
                label: 'Total watch time',
                value: this.formatDuration(summary.totalWatchSeconds),
                hint: `${this.formatNumber(summary.completion90Count)} watched 90%+`,
            },
            {
                label: 'Chat messages',
                value: this.formatNumber(summary.chatMessageCount),
                hint: `${this.formatNumber(summary.uniqueChatters)} chatters`,
            },
            {
                label: 'Poll responses',
                value: this.formatNumber(summary.pollResponseCount),
                hint: `${this.formatPercent(summary.pollResponseRate)} response rate`,
            },
            {
                label: 'Resource downloads',
                value: this.formatNumber(summary.resourceDownloadCount),
                hint: `${this.formatPercent(summary.resourceDownloadRate)} download rate`,
            },
            {
                label: 'Lead signals',
                value: this.formatNumber(summary.leadSignalCount),
                hint: 'From poll and resource engagement',
            },
        ];
    }

    get hasActivity(): boolean {
        const summary = this.summary;
        if (!summary) return false;
        return [
            summary.registrationCount,
            summary.attendeeCount,
            summary.chatMessageCount,
            summary.pollResponseCount,
            summary.resourceDownloadCount,
            summary.leadSignalCount,
        ].some((value) => Number(value) > 0);
    }

    attendeeLabel(index: number): string {
        return `Participant ${index + 1}`;
    }

    formatBool(value: boolean): string {
        return value ? 'Yes' : 'No';
    }

    formatPercent(value: number | null | undefined): string {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return 'n/a';
        }
        const normalized = Number(value);
        const percent = normalized <= 1 ? normalized * 100 : normalized;
        return `${Math.round(percent)}%`;
    }

    formatDuration(seconds: number | null | undefined): string {
        const total = Math.max(0, Math.round(Number(seconds ?? 0)));
        if (!total) return '0m';
        const hours = Math.floor(total / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        if (hours > 0) {
            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
        }
        return `${minutes}m`;
    }

    private formatNumber(value: number | null | undefined): string {
        return new Intl.NumberFormat().format(Number(value ?? 0));
    }

    private formatDateTime(value: string): string {
        const date = new Date(value);
        if (Number.isNaN(date.valueOf())) return '';
        return date.toLocaleString();
    }
}
